import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import * as Yup from 'yup';

import LiteralsService from 'src/services/literals/LiteralsService';
import { DateRangeModalViewModel } from 'src/types/view-models/transactions/date-range-modal.view.model';
import DateUtils from 'src/utils/date';
import adobeTags from 'src/types/adobe-tags';
import { trackClick } from 'src/hooks/useAdobeTags';
import { Form, Formik } from 'formik';
import Selectors from 'src/utils/selectors';
import DateRangeInput from 'src/components/formik-fields/date-range-input/DateRangeInput';
import FormatService from 'src/services/format.service';
import { LocalDate } from '@js-joda/core';
import styles from './dateRangeModal.module.scss';
import { ButtonPrimary, ButtonSecondary, Modal, ModalBody, ModalFooter, ModalHeader } from '@americanexpress/dls-react';
import { DlsTypographyVariants } from 'src/types/dls-typography-variants.enum';

/** Form validation schema */
const validationSchema = Yup.object({
    startDate: Yup.date().required(LiteralsService.getLiteral('DateRange.Modal.startDateRequired')),
    endDate: Yup.date()
        .required(LiteralsService.getLiteral('DateRange.Modal.endDateRequired'))
        .min(Yup.ref('startDate'), LiteralsService.getLiteral('FormErrors.endDateAfterStartDate'))
});

export default function DateRangeModal({ closeModal }: DateRangeModalViewModel): JSX.Element {
    const { pathname } = useLocation();

    const [customDates, setCustomDates] = useState({
        start: FormatService.formatDate(DateUtils.daysAgo(1)),
        end: FormatService.formatDate(LocalDate.now())
    });

    const handleSubmit = (): void => {
        closeModal(DateUtils.dateify(customDates.start), DateUtils.dateify(customDates.end));
        trackClick(pathname, adobeTags.Transactions.DateRangeModal.Apply);
    };

    const onClose = (): void => {
        closeModal();
        trackClick(pathname, adobeTags.Transactions.DateRangeModal.Cancel);
    };

    return (
        <Formik
            initialValues={{ startDate: customDates.start, endDate: customDates.end }}
            onSubmit={handleSubmit}
            validationSchema={validationSchema}
            enableReinitialize
        >
            {({ submitForm }) => (
                <Form data-cy={Selectors.SpendSmart.DATE_RANGE_MODAL}>
                    <Modal
                        aria-labelledby="dateRangeModal-header"
                        id="dateRangeModal"
                        className={styles.dateRangeModal}
                    >
                        <ModalHeader onClose={onClose} id="dateRangeModal-header">
                            <h3 className={`${styles.modalHeader} ${DlsTypographyVariants.HEADING_3}`}>
                                {LiteralsService.getLiteral('DateRange.Modal.header')}
                            </h3>
                        </ModalHeader>
                        <ModalBody className={styles.modalBody}>
                            <DateRangeInput
                                id="customDateFilter"
                                datesIds={{ start: 'startDate', end: 'endDate' }}
                                label={LiteralsService.getLiteral('DateRange.Modal.label')}
                                dates={customDates}
                                setDates={setCustomDates}
                                className={styles.dateRangeInput}
                            />
                        </ModalBody>
                        <ModalFooter className={styles.modalFooter}>
                            <ButtonSecondary data-cy={Selectors.SpendSmart.CLOSE_BUTTON} onClick={onClose}>
                                {LiteralsService.getLiteral('CommonText.cancel')}
                            </ButtonSecondary>
                            <ButtonPrimary data-cy={Selectors.General.SUBMIT_BUTTON} onClick={submitForm}>
                                {LiteralsService.getLiteral('CommonText.apply')}
                            </ButtonPrimary>
                        </ModalFooter>
                    </Modal>
                </Form>
            )}
        </Formik>
    );
}
